import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { API_URL } from "src/scenes/App";
import axios from "axios";
import {
    CardTableEmptyState,
    DataTable,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    DataTableRow,
    LottieLoadingIndicator,
    PortalSelect,
    SettingsInfoBox,
    SettingsPanelCaption,
    SettingsPanelHeader,
    SettingsPanelStack,
    SettingsPanelTitle
} from "src/components";
import { SettingContext } from "../SettingContext";
import moment from "moment";
import MoreMenuContainer from "../components/MoreMenuContainerBilling";
import CalendarIcon from "src/img/billingcalendar.svg";

const StatusBadge = styled.div`
    width: 88px;
    height: 28px;
    background: ${({ status }) => {
        switch (status?.toLowerCase()) {
            case "paid":
                return "#ABFFA0";
            case "unpaid":
                return "#AACEFF";
            case "overdue":
                return "#FFDBDB";
            default:
                return "#E0E0E0";
        }
    }};
    box-shadow: 0px 3px 6px #00000029;
    border: 1px solid
        ${({ status }) => {
        switch (status?.toLowerCase()) {
                case "paid":
                    return "#36BE24";
                case "unpaid":
                    return "#4D94FF";
                case "overdue":
                    return "#FF0000";
                default:
                    return "#B0B0B0";
        }
    }};
    border-radius: 7px;
    opacity: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    color: #111;
    text-transform: capitalize;
`;

const InvoiceNumber = styled.span`
    color: #5A2A7F;
    font-weight: 800;
`;

const DropdownSet = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-width: 0;

    img {
        width: 18px;
        height: 18px;
        flex: 0 0 auto;
    }
`;

const ActionCell = styled.div`
    display: flex;
    justify-content: center;
    position: relative;
`;

function BillingHistory(_props) {
    const { token } = useContext(SettingContext);
    const [invoices, setInvoices] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(true);

    const options = [
        { value: "All Time", label: "All Time" },
        { value: "This Year", label: "This Year" },
        { value: "Last Year", label: "Last Year" }
    ];
    const [indexOfShownMenu, setIndexOfShownMenu] = useState(null);

    useEffect(() => {
        fetchInvoices();
    }, []);

    // Fetch Invoices
    const fetchInvoices = async () => {
        try {
            let invoicesRes = axios.get(`${API_URL}/company/getAllInvoices`, {
                headers: {
                    Authorization: "Bearer " + token
                }
            });

            const res = await invoicesRes;
            setInvoices(res.data);
            setInvoicesLoading(false);
        } catch (error) {
            console.log(error);
        }
    };

    const handleSubmit = async (filterValue) => {
        setInvoicesLoading(true);

        let invoicesRes = axios.get(`${API_URL}/company/getAllInvoices`, {
            params: {
                filterYear: filterValue
            },
            headers: {
                Authorization: "Bearer " + token
            }
        });

        const res = await invoicesRes;
        setInvoices(res.data);
        setInvoicesLoading(false);
    };

    return (
        <SettingsPanelStack>
            <SettingsPanelHeader withDivider>
                <div>
                    <SettingsPanelTitle>Billing History</SettingsPanelTitle>
                    <SettingsPanelCaption>
                        View invoices and refunds for your business user subscriptions.
                    </SettingsPanelCaption>
                </div>

                <DropdownSet>
                    <img src={CalendarIcon} alt="" />
                    <PortalSelect onChange={(e) => handleSubmit(e.target.value)} width="12rem">
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </PortalSelect>
                </DropdownSet>
            </SettingsPanelHeader>

            {invoicesLoading ? (
                <SettingsInfoBox justifyContent="center">
                    <LottieLoadingIndicator/>
                </SettingsInfoBox>
            ) : invoices.length ? (
                <DataTableContainer minHeight="0" maxHeight="42rem">
                    <DataTable minWidth="980px" minHeight="0">
                        <thead>
                            <tr>
                                <DataTableHeaderCell>Date</DataTableHeaderCell>
                                <DataTableHeaderCell>Invoice</DataTableHeaderCell>
                                <DataTableHeaderCell>Description</DataTableHeaderCell>
                                <DataTableHeaderCell>Amount Purchased</DataTableHeaderCell>
                                <DataTableHeaderCell>Payment Method</DataTableHeaderCell>
                                <DataTableHeaderCell>Status</DataTableHeaderCell>
                                <DataTableHeaderCell textAlign="right">Total</DataTableHeaderCell>
                                <DataTableHeaderCell textAlign="center">&nbsp;</DataTableHeaderCell>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((invoice, key) => {
                                return (
                                    <DataTableRow key={invoice.uuid || key}>
                                        <DataTableCell>
                                            {moment(invoice.date).format("MMM D, YYYY")}
                                        </DataTableCell>
                                        <DataTableCell>
                                            <InvoiceNumber>{invoice.invoiceNo}</InvoiceNumber>
                                        </DataTableCell>
                                        <DataTableCell whiteSpace="normal">
                                            {invoice.description}
                                        </DataTableCell>
                                        <DataTableCell>
                                            {invoice.numberOfUsers}
                                        </DataTableCell>
                                        <DataTableCell>
                                            {invoice.paymentMethod}
                                        </DataTableCell>
                                        <DataTableCell>
                                            <StatusBadge status={invoice.status}>
                                                {invoice.status}
                                            </StatusBadge>
                                        </DataTableCell>
                                        <DataTableCell textAlign="right">
                                            {invoice.currency} {invoice.totalIncGST}
                                        </DataTableCell>
                                        <DataTableCell textAlign="center" $last>
                                            <ActionCell>
                                                <MoreMenuContainer thisIndex={key} indexOfShownMenu={indexOfShownMenu} setIndexOfShownMenu={setIndexOfShownMenu} invoice={invoice} token={token} />
                                            </ActionCell>
                                        </DataTableCell>
                                    </DataTableRow>
                                );
                            })}
                        </tbody>
                    </DataTable>
                </DataTableContainer>
            ) : (
                <CardTableEmptyState minHeight="12rem">
                    No billing history is available yet.
                </CardTableEmptyState>
            )}
        </SettingsPanelStack>
    );
}

export default BillingHistory;
